import { Syndication } from '../types/syndication';

export const calculateNetGainForSyndicationsThisYear = (syndications: Syndication[], cocReturn: number, currentYear: number, previousYearCarriedLoss: number) => {
    const allDistributions = calculateYearlyDistributionsForSyndications(syndications, cocReturn);
    const allDepreciation = syndications.reduce((total, current) => total + calculateDepreciationForYear(currentYear - current.startYear, current.amountInvested), 0);

    const totalLosses = allDepreciation + previousYearCarriedLoss;
    const netGain = allDistributions - totalLosses;

    return netGain;
}

export const calculateYearlyDistributionsForSyndications = (syndications: Syndication[], cocReturn: number) => {
    return syndications.reduce((total, current) => total + current.amountInvested * cocReturn, 0);
}

const DEPRECIATION_SCHEDULE = [0.33, 0.33, 0.25, 0.20, 0.10];

export const calculateDepreciationForYear = (yearNumber: number, amountInvested: number) => {
    if (yearNumber < 0) return 0;
    
    // If years held is beyond the schedule length, use the last value
    const scheduleIndex = Math.min(yearNumber, DEPRECIATION_SCHEDULE.length - 1);
    return amountInvested * DEPRECIATION_SCHEDULE[scheduleIndex];
};

export const calculateTotalDepreciation = (syndication: Syndication, currentYear: number) => {
    const yearsHeld = currentYear - syndication.startYear;
    if (yearsHeld < 0) return 0;
    
    let total = 0;
    for (let i = 0; i <= Math.min(yearsHeld, DEPRECIATION_SCHEDULE.length - 1); i++) {
        total += syndication.amountInvested * DEPRECIATION_SCHEDULE[i];
    }
    return total;
};

export const calculateYearlyDistributionsForPosition = (position: number, cocReturn: number) => {
    return position * cocReturn;
}

export const calculateCarriedLossFromNetGain = (netGain: number) => {
    return Math.max(0, -1 * netGain);
}

type SyndicationWithOrWithoutCarriedLoss = Omit<Syndication, 'carriedLoss'> & {carriedLoss?: number};

export const getSyndicationsWithCarriedLoss = (syndications: SyndicationWithOrWithoutCarriedLoss[], cocReturn: number, currentYear: number): Syndication[] => {
    const lossMap = syndications.reduce<Record<string, number>>((acc, syndication) => {
        const currentYearLoss = Math.min(0, 
            calculateYearlyDistributionsForPosition(syndication.amountInvested, cocReturn) - calculateDepreciationForYear(currentYear - syndication.startYear, syndication.amountInvested)
        ) * -1;
        const pastCarriedLoss = syndication.carriedLoss || 0;
        acc[syndication.id] = pastCarriedLoss + currentYearLoss;
        return acc;
    }, {});

    return syndications.map(syndication => ({
        ...syndication,
        carriedLoss: lossMap[syndication.id]
    }));
}